import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, History, Instagram, Twitter, Youtube } from 'lucide-react'
import { useMember } from '@/hooks/use-members'
import { formatDate } from '@/lib/utils'

type SocialLinks = {
  instagram?: string
  twitter?: string
  youtube?: string
  tiktok?: string
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
        {children}
      </h2>
      <div className="h-px flex-1 bg-border" />
    </div>
  )
}

export default function MemberDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: member, isLoading } = useMember(id)

  if (isLoading) {
    return <p className="text-muted-foreground">Memuat...</p>
  }
  if (!member) {
    return <p className="text-muted-foreground">Member tidak ditemukan.</p>
  }

  const displayName = member.stage_name ?? member.name
  const initial = displayName.charAt(0).toUpperCase()
  const socialLinks = member.social_links as SocialLinks | null

  return (
    <div className="max-w-3xl space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => navigate('/members')}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/members/${id}/cheki`)}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <History className="h-4 w-4" />
            Riwayat Cheki
          </button>
          <button
            type="button"
            onClick={() => navigate(`/members/${id}/edit`)}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        </div>
      </div>

      {/* Profile card */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
        {/* Personal color bar */}
        {member.personal_color && (
          <div className="h-1.5 w-full" style={{ backgroundColor: member.personal_color }} />
        )}

        <div className="flex flex-col sm:flex-row gap-6 p-6">
          {/* Photo */}
          <div className="shrink-0 mx-auto sm:mx-0">
            <div className="h-36 w-36 rounded-2xl overflow-hidden bg-muted flex items-center justify-center text-5xl font-bold text-muted-foreground/40 select-none">
              {member.photo_url ? (
                <img
                  src={member.photo_url}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span>{initial}</span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex flex-wrap items-start gap-2">
              <h1 className="text-2xl font-bold leading-tight">{displayName}</h1>
              <span
                className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                  member.is_active
                    ? 'bg-green-100 text-green-700'
                    : 'bg-zinc-100 text-zinc-500'
                }`}
              >
                {member.is_active ? 'Aktif' : 'Nonaktif'}
              </span>
            </div>

            {member.stage_name && member.stage_name !== member.name && (
              <p className="text-sm text-muted-foreground">{member.name}</p>
            )}

            {(member.position || member.generation) && (
              <p className="text-sm text-muted-foreground">
                {[member.position, member.generation ? `Gen ${member.generation}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}

            <div className="mt-2 grid grid-cols-1 gap-1.5 text-sm">
              {member.personal_color && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-28 shrink-0">Personal Color</span>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="h-4 w-4 rounded-full border border-border/50 shrink-0"
                      style={{ backgroundColor: member.personal_color }}
                    />
                    <span className="font-mono text-xs">{member.personal_color}</span>
                  </div>
                </div>
              )}
              {member.element && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-28 shrink-0">Elemen</span>
                  <span>{member.element}</span>
                </div>
              )}
              {member.birthday && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-28 shrink-0">Lahir</span>
                  <span>{formatDate(member.birthday, { dateStyle: 'long', timeStyle: undefined })}</span>
                </div>
              )}
              {member.joined_at && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-28 shrink-0">Bergabung</span>
                  <span>{formatDate(member.joined_at, { dateStyle: 'long', timeStyle: undefined })}</span>
                </div>
              )}
              {member.left_at && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-28 shrink-0">Keluar</span>
                  <span>{formatDate(member.left_at, { dateStyle: 'long', timeStyle: undefined })}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bio */}
      {member.bio && (
        <section>
          <SectionTitle>Bio</SectionTitle>
          <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{member.bio}</p>
        </section>
      )}

      {/* Jikoushokai */}
      {member.jikoushokai && (
        <section>
          <SectionTitle>Jikoushokai</SectionTitle>
          <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{member.jikoushokai}</p>
        </section>
      )}

      {/* Social links */}
      {socialLinks && Object.values(socialLinks).some(Boolean) && (
        <section>
          <SectionTitle>Media Sosial</SectionTitle>
          <div className="flex flex-wrap gap-3">
            {socialLinks.instagram && (
              <a
                href={`https://instagram.com/${socialLinks.instagram.replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Instagram className="h-4 w-4" />
                @{socialLinks.instagram.replace(/^@/, '')}
              </a>
            )}
            {socialLinks.twitter && (
              <a
                href={`https://twitter.com/${socialLinks.twitter.replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Twitter className="h-4 w-4" />
                @{socialLinks.twitter.replace(/^@/, '')}
              </a>
            )}
            {socialLinks.youtube && (
              <a
                href={`https://youtube.com/@${socialLinks.youtube.replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Youtube className="h-4 w-4" />
                @{socialLinks.youtube.replace(/^@/, '')}
              </a>
            )}
            {socialLinks.tiktok && (
              <a
                href={`https://tiktok.com/@${socialLinks.tiktok.replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <span className="text-xs font-bold">TT</span>
                @{socialLinks.tiktok.replace(/^@/, '')}
              </a>
            )}
          </div>
        </section>
      )}
    </div>
  )
}
